/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { motion } from 'motion/react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'accent' | 'success' | 'warning' | 'danger' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  className = '',
  disabled,
  ...props
}) => {
  const baseStyle = "inline-flex items-center justify-center font-semibold rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer tracking-wide";
  
  const variants = {
    primary: "bg-brand-navy text-white hover:bg-slate-800 focus:ring-slate-900 border border-slate-700",
    secondary: "bg-brand-gold text-slate-950 hover:bg-yellow-600 focus:ring-amber-500",
    accent: "bg-brand-yellow text-slate-950 hover:bg-yellow-500 focus:ring-yellow-400",
    success: "bg-brand-success text-white hover:bg-emerald-600 focus:ring-emerald-500",
    warning: "bg-brand-warning text-white hover:bg-amber-600 focus:ring-amber-500",
    danger: "bg-brand-danger text-white hover:bg-rose-600 focus:ring-rose-500",
    outline: "bg-transparent border border-border-main text-text-main hover:bg-bg-base focus:ring-slate-400",
    ghost: "bg-transparent text-text-muted hover:bg-bg-base hover:text-text-main focus:ring-slate-400"
  };

  const sizes = {
    sm: "px-4 py-2 text-[14px]",
    md: "px-6 py-3 text-[16px]",
    lg: "px-8 py-4 text-[18px]"
  };

  return (
    <motion.button
      type={props.type || 'button'}
      whileTap={disabled || isLoading ? {} : { scale: 0.98 }}
      className={`${baseStyle} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}
      {children}
    </motion.button>
  );
};
